import React, { useState } from 'react';
import { graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled from 'styled-components';

import Insight from '../models/Insight/Insight';
import InsightPropTypes from '../models/Insight/InsightPropTypes';

import JsonLd from '../components/jsonld.jsx';
import Authors from '../components/authors';
import Layout from '../components/layout';
import RelatedInsights from '../components/related-insights';
import ContactForm from '../components/contact-form';
import LegacyGatedContent from '../components/legacy-gated-content';
import SEO from '../components/seo';
import HeadingCategoryAndDate from '../components/heading-category-and-date';
import CanonicalLink from '../components/canonical-link';

import { SocialIcons } from '../ui-components/components/social-icons';
import { colors, Container, Heading, Paper, InternalLink, fontSizes, media } from '../ui-components';
import { secondaryButtonStyles } from '../ui-components/components/component-styles/button';

import AudioPlayer from 'react-h5-audio-player';
import soundWave from '../images/PLACEHOLDER/soundWave.png';
import 'react-h5-audio-player/lib/styles.css';

import renderRichText from '../utils/render-rich-text';
import { extractGlobalEdges } from '../utils/cross-space-reference-helper.jsx';
import { checkUrlLocale } from '../utils/check-url-locale';

const renderer = require('@contentful/rich-text-plain-text-renderer');

const propTypes = {
  data: InsightPropTypes.isRequired
};

/**
 * Component that renders an Insight's content
 *
 * @component InsightDetail
 *
 * @param {object} props
 *
 * @prop {object} data
 * @prop {object} pageContext
 *
 * @returns {JSX}
 */
const InsightDetail = props => {
  const [seoSlug, setSeoSlug] = useState('');
  const { data, pageContext } = props;
  const insight = new Insight(data.contentfulInsight, data.defaultBG);
  const spaceId = pageContext.spaceId;
  const space = pageContext.space;
  const isRegionalContent = pageContext.spaceId != process.env.CONTENTFUL_GLOBAL_SPACE_ID;
  const handleSeoSlugChange = value => {
    setSeoSlug(value);
  };
  let relatedGlobalInsights = [];

  // If this is a global case study, we don't need to look by global nodes
  if (spaceId !== process.env.CONTENTFUL_GLOBAL_SPACE_ID) {
    const insights = pageContext.insights;
    const globalInsights = data.globalInsights;

    let matchedInsights = extractGlobalEdges(insights, globalInsights);
    relatedGlobalInsights = matchedInsights.map(insight => new Insight(insight.node, data.defaultBG));
  }

  let relatedInsights = insight.relatedInsights ? insight.relatedInsights : [];
  insight.relatedInsights = relatedInsights.concat(relatedGlobalInsights);

  /**
   * Helper function to format the insight content to plain text if it is not undefined
   *
   * @function getInsightText
   *
   * @returns {string} plain text string of the rendered JSON content
   */
  const getInsightText = () => {
    return insight.blogRichJSON ? renderer.documentToPlainTextString(insight.blogRichJSON) : '';
  };

  /**
   * Renders the SEO meta tag and structured data elements
   *
   * @function renderSEO
   *
   * @returns {JSX.Element}
   */
  const renderSEO = () => {
    const jsonItems = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      name: insight.metaTitle,
      articleSection: insight.category,
      description: insight.metaDescription,
      abstract: insight.snippet,
      headline: insight.title,
      identifier: insight.slug,
      inLanguage: 'en',
      url: `http://credera.com/insights/${insight.slug}`,
      text: getInsightText(),
      isAccessibleForFree: true,
      dateModified: insight.getFormattedModificationDate('YYYY-MM-DD'),
      dateCreated: insight.getFormattedCreationDate('YYYY-MM-DD'),
      publisher: 'https://www.credera.com',
      image: insight.heroImageData?.fluid?.images?.fallback?.src || null
    };

    if (insight.publishDate) {
      jsonItems.datePublished = insight.getFormattedPublishDate('YYYY-MM-DD');
    }

    return (
      <>
        <SEO
          title={insight.metaTitle}
          description={`${insight.metaDescription} ${insight?.podcastAudio?.description || ''}`}
          slug={seoSlug}
          image={insight.heroImageData?.fluid?.images?.fallback?.src || null}
        />
        <CanonicalLink
          isRegionalContent={isRegionalContent}
          contentType={'insights'}
          contentSlug={insight.slug}
          onSeoSlugChange={handleSeoSlugChange}
        />
        <JsonLd item={jsonItems} />
      </>
    );
  };

  /**
   * Renders the insights list of tags at the bottom of the page
   *
   * @function renderInsightTags
   *
   * @returns {JSX.Element}
   */
  const renderInsightTags = () => {
    let renderTags;

    renderTags = insight.tags.map((tag, index) => {
      return (
        <TagListItem key={index}>
          <a href={`${checkUrlLocale()}/search/?filter=insights&query=${encodeURIComponent(tag.title)}`}>{tag.title}</a>
        </TagListItem>
      );
    });

    return renderTags;
  };

  const Player = () => (
    <div
      style={{ display: 'flex', justifyContent: 'center' }}
      title={insight.podcastAudio.description || 'Credera.com Technically Minded Podcast'}
    >
      <AudioPlayer
        autoPlay={false}
        src={insight.podcastAudio.file.url}
        style={{
          width: '95%',
          height: '125px',
          margin: '20px',
          borderRadius: '10px',
          backgroundImage: `url(${soundWave})`
        }}
        showSkipControls={false}
        customAdditionalControls={[]}
        header={insight.podcastAudio.title}
      />
    </div>
  );

  /**
   * Renders the hero section
   *
   * @function renderHeroSection
   *
   * @returns {JSX.Element}
   */
  const renderHeroSection = () => {
    const { title, authors } = insight;

    return (
      <Paper bottom overlay>
        <Container condensed>
          <TopIcons>
            <InternalLink withArrow backArrow hoverUnderline to="/insights">
              Back to insights
            </InternalLink>
            <SocialIcons space={space} insight={insight} />
          </TopIcons>
          <HeadingCategoryAndDateContainer>
            <HeadingCategoryAndDate insight={insight} />
          </HeadingCategoryAndDateContainer>
          <Heading level={1}>{title}</Heading>
          <AuthorsGroup authors={authors} />
        </Container>
      </Paper>
    );
  };

  return (
    <Layout style={{ scrollBehavior: 'smooth' }}>
      {renderSEO()}
      {renderHeroSection()}
      <Container condensed className="insight-body">
        {insight.heroImageData?.fluid ? (
          <ImgMain
            loading="eager"
            fetchpriority="high"
            image={insight.heroImageData.fluid}
            alt={insight.heroImageData.title}
          />
        ) : (
          <ImgMain
            loading="eager"
            fetchpriority="high"
            image={insight.heroDefaultImageData.fluid}
            alt={insight.heroDefaultImageData.title}
          />
        )}
        {insight.podcastAudio && Player()}
        {insight.blogRichJSON ? renderRichText(insight.blogRichJSON, pageContext.space, 'insight') : ''}
        <TagList>{renderInsightTags()}</TagList>
        {(insight.downloadableContent?.url || insight.videoEmbedUrl || insight.redirectContentUrl) && (
          <LegacyGatedContent
            type={insight.type}
            requireForm={insight.requireContentForm}
            attachment={insight.downloadableContent?.url ? insight.downloadableContent : insight.videoEmbedUrl}
            redirectContentUrl={insight?.redirectContentUrl}
            title={insight.contentTitle}
            completedFormPrompt={insight.completedFormPrompt}
            geoSpace={pageContext.space}
            slug={pageContext.slug}
            customForm={insight.customForm}
          />
        )}
      </Container>
      {insight.relatedInsights && insight.relatedInsights.length > 0 && (
        <Paper top>
          <RelatedInsights insights={insight.relatedInsights} />
        </Paper>
      )}
      {!insight.requireContentForm && (
        <ContactForm geoSpace={pageContext?.space} slug={pageContext?.slug} customForm={insight?.customForm} />
      )}
    </Layout>
  );
};

const HeadingCategoryAndDateContainer = styled(Container)`
  padding: 24px 0 4px 0;
`;

const AuthorsGroup = styled(props => <Authors {...props} />)`
  margin-top: 40px;
`;

const ImgMain = styled(props => <GatsbyImage {...props} />)`
  margin-bottom: 40px;
  box-shadow: 2px 2px 14px -1px rgba(58, 58, 58, 0.75);
`;

const TagList = styled.ul`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  list-style-type: none;
  padding: 0;
  margin: 0;
`;

const TagListItem = styled.li`
  ${secondaryButtonStyles};
  padding: 8px 16px;
  border: 1px solid ${colors.gray3};
  border-radius: 8px;
  flex: 0 1 auto;
  font-size: ${fontSizes.subtitle.small.fontSize};
  line-height: ${fontSizes.subtitle.small.lineHeight};
  font-weight: 400;
  margin-right: 8px;
  text-align: left;
  text-transform: none;
  font-family: Lato, sans-serif;
  
  @media only screen and (max-width: ${media.mediumTop}) {
    font-size: ${fontSizes.subtitle.small.mobile.fontSize}
    line-height: ${fontSizes.subtitle.small.mobile.lineHeight}
  }
`;

const TopIcons = styled.div`
  display: flex;
  justify-content: space-between;
`;

InsightDetail.propTypes = propTypes;

export default InsightDetail;

export const insightDetailPageQuery = graphql`
  query InsightDetailPageQuery(
    $slug: String!
    $spaceId: String!
    $insightFilter1: String
    $insightFilter2: String
    $insightFilter3: String
    $globalSpaceId: String!
  ) {
    contentfulInsight(slug: { eq: $slug }, spaceId: { eq: $spaceId }) {
      ...CompleteInsight
    }
    defaultBG: file(relativePath: { eq: "default-banner.png" }) {
      childImageSharp {
        gatsbyImageData(layout: CONSTRAINED, width: 800)
      }
    }
    globalInsights: allContentfulInsight(
      filter: {
        spaceId: { eq: $globalSpaceId }
        contentful_id: { in: [$insightFilter1, $insightFilter2, $insightFilter3] }
      }
    ) {
      edges {
        node {
          ...NoContentInsight
        }
      }
    }
  }
`;
